import { getUser } from "../apiConfig";

const axios = require("axios");

const apiRoot = (window as any).Configs.MAP_RELEASE_API_ROOT;

const STATION_RECOSTING_ENDPOINT = "stationRecostingSummary?mapPackSummaryId=";
const STATION_SIMULATION_ENDPOINT = "stationSimulationSummary?mapPackSummaryId="
const MAP_PACK_VALIDATION_ENDPOINT = "mapPackValidationSummary?region=";
const MAP_PACK_BEDROCK_ENDPOINT = "mapPackLLMSummary";
const MAP_PACK_RELEASE_ENDPOINT = "mapPackRelease";
const MAP_PACK_CONSENSUS_ENDPOINT = "mapPackConsensus";
const MAP_PACK_CONSENSUS_APPROVAL_ENDPOINT = "mapPackConsensus?region=";
const MAP_PACK_RELEASE_STATUS_ENDPOINT = "mapPackRelease?region=";
const MAP_PACK_MCM_ENDPOINT = "mapPackMCM";

/**
 * @param summaryId - identifier for the recosting summary
 * @returns station level recosting summary
 */
export const getStationRecostingSummary = async (summaryId: string | null) => {
  try {
    const user = getUser();
    const token = user?.access_token;
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const url = apiRoot + STATION_RECOSTING_ENDPOINT + summaryId;

    const res = await axios.get(url, config);

    return res.data;
  } catch (error) {
    console.error(error);
    return {
      message: error,
    };
  }
};

/**
 * @param region - region of the map pack validation summary
 * @returns map pack level validation summary
 */
export const getMapPackValidationSummary = async (region: string) => {
  try {
    const user = getUser();
    const token = user?.access_token;
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const url = apiRoot + MAP_PACK_VALIDATION_ENDPOINT + region;

    const res = await axios.get(url, config);

    return res.data;
  } catch (error) {
    console.error(error);
    return {
      message: error,
    };
  }
};

/**
 * @returns map pack bedrock summary
 */
export const getMapPackBedrockSummary = async (requestBody: any) => {
  try {
    const user = getUser();
    const token = user?.access_token;
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const url = apiRoot + MAP_PACK_BEDROCK_ENDPOINT;

    const res = await axios.post(url, requestBody, config);

    return res.data;
  } catch (error: any) {
    console.error("Error when fetching Bedrock results: ", error);
    if (error.response.status === 504 || error.response.status === 429) { // http timeout & too many tokens
      throw error
    } else {
      return {
        message: error,
      };
    }
  }
};
/**
 * @returns release mappack version
 */
export const releaseMapPack = async (requestBody: any) => {
  try {
    const user = getUser();
    const token = user?.access_token;
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const url = apiRoot + MAP_PACK_RELEASE_ENDPOINT;

    const res = await axios.post(url, requestBody, config);
    return res.data;
  } catch (error) {
    console.error(error);
    return {
      message: error,
    };
  }
};
/**
 * @param requestBody - request body for Consensus API
 * @returns consensus link
 */
export const getConsensusApprovalLink = async (requestBody: any) => {
  try {
    const user = getUser();
    const token = user?.access_token;
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const url = apiRoot + MAP_PACK_CONSENSUS_ENDPOINT;

    const res = await axios.post(url, requestBody, config);
    return res.data;
  } catch (error) {
    console.error(error);
    return {
      message: error,
    };
  }
};

/**
 * Check if MCM URL exists for given RSM version
 * @param rsmVersion - RSM version to check
 * @returns boolean indicating if MCM URL exists
 */
export const checkMCMExists = async (rsmVersion: string): Promise<boolean> => {
  try {
    const user = getUser();
    const token = user?.access_token;
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const url = apiRoot + MAP_PACK_MCM_ENDPOINT + "?checkMCM=true&rsmVersion=" + rsmVersion;
    const requestBody = {}; 
    const res = await axios.post(url, requestBody, config);
    return res.data.mcmExists || false;
  } catch (error) {
    console.error("Error in checkMCMExists:", error);
    return false;
  }
};

/**
 * @param summaryId - identifier for the simulation summary
 * @returns station level simulation summary
 */
export const getStationSimulationSummary = async (summaryId: string | null) => {
  try {
    const user = getUser();
    const token = user?.access_token;
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const url = apiRoot + STATION_SIMULATION_ENDPOINT + summaryId;

    const res = await axios.get(url, config);

    return res.data;
  } catch (error) {
    console.error(error);
    return {
      message: error,
    };
  }
};

/**
 * @param region - region of the map pack consensus ticket
 * @returns consensus approval status
 */
export const getConsensusApprovalStatus = async (region: string) => {
  try {
    const user = getUser();
    const token = user?.access_token;
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const url = apiRoot + MAP_PACK_CONSENSUS_APPROVAL_ENDPOINT + region;
    const requestBody = {};
    const res = await axios.post(url, requestBody, config);
    return res.data;
  } catch (error) {
    console.error(error);
    return {
      message: error,
    };
  }
};

/**
 * @param region - region of the map pack
 * @returns mappack release status
 */
export const getReleaseStatus = async (region: string) => {
  try {
    const user = getUser();
    const token = user?.access_token;
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const url = apiRoot + MAP_PACK_RELEASE_STATUS_ENDPOINT + region;
    const requestBody = {};
    const res = await axios.post(url, requestBody, config);
    return res.data;
  } catch (error) {
    console.error(error);
    return {
      message: error,
    };
  }
};

/**
 * @param requestBody - request body for MCM API
 * @returns MCM ticket link
 */
export const getMCMLink = async (requestBody: string) => {
  try {
    const user = getUser();
    const token = user?.access_token;
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const url = apiRoot + MAP_PACK_MCM_ENDPOINT;
    const res = await axios.post(url, requestBody, config);
    return res.data;
  } catch (error) {
    console.error(error);
    return {
      message: error,
    };
  }
};

/**
 * @param mapPack - mapPack RSM version 
 * @param station - SPOC adjustment station
 * @param method - SPOC adjustment method
 * @param currSPOC - current SPOC
 * @param newSPOC - new SPOC
 * @returns MCM ticket link
 */
export const addSPOCAdjustment = async (mapPack: string, station: string, method: string, currSPOC: string, newSPOC: string) => {
  try {
    const user = getUser();
    const token = user?.access_token;
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const url = apiRoot + MAP_PACK_MCM_ENDPOINT + "?mapPack=" + mapPack + "&station=" + station + "&method=" + method  + "&currSPOC=" + currSPOC + "&newSPOC=" + newSPOC;
    const res = await axios.post(url, {}, config);
    return res.data;
  } catch (error) {
    console.error(error);
    return {
      message: error,
    };
  }
};

/**
 * @param mapPack - mapPack RSM version 
 * @returns existing SPOC adjustment values
 */
export const getSPOCAdjustments = async (mapPack: string) => {
  try {
    const user = getUser();
    const token = user?.access_token;
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const url = apiRoot + MAP_PACK_MCM_ENDPOINT + "?getSPOCs=true&mapPack=" + mapPack;
    const res = await axios.post(url, {}, config);
    return res.data;
  } catch (error) {
    console.error(error);
    return {
      message: error,
    };
  }
};

/**
 * Update consensus approval status for testing purposes
 * @param mapPackString - map pack string
 * @param ruleName - approval rule name
 * @param approverAlias - approver alias
 * @param approved - whether approved or not
 * @returns update result
 */
export const updateConsensusApprovalStatus = async (mapPackString: string, ruleName: string, approverAlias: string, approved: boolean) => {
  try {
    const user = getUser();
    const token = user?.access_token;
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const url = apiRoot + MAP_PACK_CONSENSUS_ENDPOINT + "?updateApproval=true";
    const requestBody = {
      mapPackString,
      ruleName,
      approverAlias,
      approved
    };
    const res = await axios.put(url, requestBody, config);
    return res.data;
  } catch (error) {
    console.error(error);
    return {
      message: error,
    };
  }
};
